import React from 'react';
import { Star } from 'lucide-react';
import LivePrice from './LivePrice';
import BotStatusChip from './BotStatusChip';
import { VerdictChip, EXPLOSION_STYLE, fmtPrice } from './Verdict';

const pct = (v) => (v === null || v === undefined ? '—' : `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(1)}%`);

/** Patlama ihtimali: yüzde + dayanak (geçmiş veriden); yüzde yoksa kelime. */
export const Explosion = ({ v, symbol }) => (
    <span className="text-xs text-slate-400" title={v.explosion_basis_tr || ''} data-testid={symbol ? `explosion-${symbol}` : undefined}>
        Patlama: <b className={EXPLOSION_STYLE[v.explosion]}>{v.explosion_label_tr || v.explosion_tr}</b>
        {v.explosion_estimated && <span className="text-[10px] text-slate-500"> (tahmini)</span>}
    </span>
);

const Power = ({ n }) => (
    <span className="font-mono tracking-widest text-amber-300" title={`Güç ${n}/5`} aria-label={`Güç ${n} / 5`}>
        {'●'.repeat(n)}<span className="text-slate-600">{'●'.repeat(5 - n)}</span>
    </span>
);

const Level = ({ label, value, sub, tone = 'text-slate-50', testId }) => (
    <div className="rounded-md bg-slate-950/60 border border-slate-700/70 px-2.5 py-1.5 min-w-0" data-testid={testId}>
        <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className={`font-mono text-sm font-semibold ${tone} truncate`}>{value}</div>
        {sub && <div className={`text-[11px] font-mono ${tone}`}>{sub}</div>}
    </div>
);

const RING = { AC: 'border-emerald-500/40', KUCUK_AC: 'border-sky-500/40', EMIR_KOY: 'border-amber-500/40', BEKLE: 'border-slate-600', KOVALAMA: 'border-orange-500/30' };

/** Big, plain card: WHO · WHICH WAY · WHAT TO DO · WHEN · LEVELS · WHY — all from row.verdict. */
export default function CandidateRow({ c, onSelect, tick, bot, block }) {
    const { v, row: r } = c;
    const isLong = v.direction === 'long';
    const ch = Number(r.change_pct_24h || 0);
    const L = v.levels;
    const actionable = v.action === 'AC' || v.action === 'KUCUK_AC' || v.action === 'EMIR_KOY';
    if (v.action === 'BEKLE') {
        return (
            <button
                type="button"
                onClick={() => onSelect?.(c.symbol)}
                data-testid={`candidate-row-${c.symbol}`}
                className={`w-full text-left rounded-xl border bg-slate-900/60 hover:bg-slate-900 transition-colors px-3 py-2 ${RING.BEKLE}`}
            >
                <div className="flex items-center gap-2 flex-wrap">
                    {r.is_favorite && <Star className="h-3.5 w-3.5 text-amber-300 fill-amber-300 shrink-0" />}
                    <span className="font-mono font-bold text-base text-slate-50">{c.symbol.replace('USDT', '')}</span>
                    <span data-testid={`candidate-dir-${c.symbol}`} className={`rounded-md px-1.5 py-0.5 text-[11px] font-bold ${isLong ? 'bg-emerald-500/20 text-emerald-200' : 'bg-rose-500/20 text-rose-200'}`}>
                        {isLong ? '▲ YUKARI' : '▼ AŞAĞI'}
                    </span>
                    <VerdictChip v={v} size="sm" testId={`candidate-stage-${c.symbol}`} />
                    <Explosion v={v} symbol={c.symbol} />
                    {bot && <BotStatusChip bot={bot} symbol={c.symbol} />}
                    <span className="ml-auto flex items-center gap-2 font-mono text-xs text-slate-100">
                        <LivePrice symbol={c.symbol} tick={tick} fallbackPrice={r.price} />
                        <span className={ch >= 0 ? 'text-emerald-300' : 'text-rose-300'}>{ch >= 0 ? '+' : ''}{ch.toFixed(1)}%</span>
                    </span>
                </div>
                <div className="text-xs text-slate-300 mt-1 truncate" data-testid={`candidate-when-${c.symbol}`}>
                    <span className="text-slate-500">Ne zaman: </span>{v.when_tr}
                </div>
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={() => onSelect?.(c.symbol)}
            data-testid={`candidate-row-${c.symbol}`}
            className={`w-full text-left rounded-xl border bg-slate-900/60 hover:bg-slate-900 transition-colors p-3 sm:p-4 space-y-2.5 ${RING[v.action] || RING.BEKLE}`}
        >
            <div className="flex items-center gap-2 flex-wrap">
                {r.is_favorite && <Star className="h-3.5 w-3.5 text-amber-300 fill-amber-300 shrink-0" />}
                <span className="font-mono font-bold text-lg text-slate-50">{c.symbol.replace('USDT', '')}</span>
                <span
                    data-testid={`candidate-dir-${c.symbol}`}
                    className={`rounded-md px-2 py-0.5 text-xs font-bold ${isLong ? 'bg-emerald-500/20 text-emerald-200' : 'bg-rose-500/20 text-rose-200'}`}
                >
                    {isLong ? '▲ YUKARI · LONG' : '▼ AŞAĞI · SHORT'}
                </span>
                <span className="ml-auto flex items-center gap-2 font-mono text-sm text-slate-100">
                    <LivePrice symbol={c.symbol} tick={tick} fallbackPrice={r.price} />
                    <span className={`text-xs ${ch >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>{ch >= 0 ? '+' : ''}{ch.toFixed(1)}% bugün</span>
                </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <VerdictChip v={v} size="lg" testId={`candidate-stage-${c.symbol}`} />
                <span className="text-sm text-slate-200">{v.title_tr}</span>
                <span className="ml-auto text-xs text-slate-400 flex items-center gap-2">
                    <Explosion v={v} symbol={c.symbol} />
                    <span className="flex items-center gap-1">Güç <Power n={v.power || 1} /></span>
                </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap" data-testid={`candidate-bot-${c.symbol}`}>
                <BotStatusChip bot={bot} block={block} actionable={actionable} symbol={c.symbol} size="lg" />
                {r.radar?.short_id && <span className="text-[11px] font-mono text-slate-400">sinyal #{r.radar.short_id}</span>}
            </div>

            <div className="text-sm text-slate-100" data-testid={`candidate-when-${c.symbol}`}>
                <span className="text-slate-400">Ne zaman: </span>{v.when_tr}
                {bot?.state === 'pending' && bot.trigger != null && (
                    <span className="block text-[11px] text-amber-200/90">Bot emri {fmtPrice(bot.trigger)} = tetik + ATR payı (sahte kırılım filtresi); 15 dk mum hacimle kapatırsa daha erken açar.</span>
                )}
            </div>

            {L ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`candidate-levels-${c.symbol}`}>
                    <Level label="Giriş" value={fmtPrice(L.entry)} />
                    <Level label="Zarar kes (stop)" value={fmtPrice(L.stop)} sub={pct(L.stop_pct)} tone="text-rose-200" />
                    <Level label="Hedef (TP1)" value={fmtPrice(L.tp)} sub={pct(L.tp_pct)} tone="text-emerald-200" />
                    <Level label="Kaldıraç" value={bot?.leverage ? `${bot.leverage}x` : (L.leverage || '1x')} sub={bot?.leverage ? `bot · plan ${L.leverage || '1x'}` : 'plan'} tone="text-amber-200" />
                </div>
            ) : (
                <div className="text-xs text-slate-500" data-testid={`candidate-levels-${c.symbol}`}>Seviye yok — bu aşamada işlem planı çıkmaz.</div>
            )}

            <div className="text-xs text-slate-300 leading-snug" data-testid={`candidate-why-${c.symbol}`}>
                <span className="text-slate-500">Neden: </span>{v.why_tr}
                {v.explosion_basis_tr && v.explosion_pct != null && <span className="text-slate-500"> · Patlama %{v.explosion_pct}: {v.explosion_basis_tr}</span>}
            </div>
            <div className="text-[11px] text-slate-500">📲 {v.telegram_tr}</div>
        </button>
    );
}
